import React, { FunctionComponent, useState } from 'react'
import styles from './DatePicker.module.sass'
import DayPicker, { DayPickerProps, Modifier, NavbarElementProps } from 'react-day-picker'
import type { ClassNames } from 'react-day-picker/types/ClassNames'
import Button from '../../../controls/Button/Button'
import { MONTHS, WEEKDAYS_LONG, WEEKDAYS_SHORT } from '../../../../utils/date-utils'
import { max, min, ordDate } from 'fp-ts/es6/Ord'
import { isSameDay } from 'date-fns'
import clsx from 'clsx'
import ChevronLeftIcon from 'files/chevron/left.svg'
import ChevronRightIcon from 'files/chevron/right.svg'

export type DateTuple = [Date | undefined, Date | undefined]

const minDate = (first?: Date, second?: Date) => {
    if (!first) {
        return second
    }
    if (!second) {
        return first
    }
    return min(ordDate)(first, second)
}

const maxDate = (first?: Date, second?: Date) => {
    if (!first) {
        return second
    }
    if (!second) {
        return first
    }
    return max(ordDate)(first, second)
}

const NavbarElement: FunctionComponent<NavbarElementProps> = props => <div className={styles.navBar}>
    <Button onClick={() => props.onPreviousClick()}>
        <ChevronLeftIcon/>
    </Button>
    <Button onClick={() => props.onNextClick()}>
        <ChevronRightIcon/>
    </Button>
</div>

const defaultProps: Partial<DayPickerProps> = {
    firstDayOfWeek: 1,
    months: MONTHS,
    weekdaysLong: WEEKDAYS_LONG,
    weekdaysShort: WEEKDAYS_SHORT,
    classNames: styles as unknown as ClassNames,
    navbarElement: NavbarElement,
}

interface DatePickerProps {
    className?: string,
    value?: Date | null,
    numberOfMonths?: number,
    onChange?: (date: Date) => void,
    disabledDays?: Modifier,
}

const DatePicker: FunctionComponent<DatePickerProps> = props => {
    return <DayPicker
        {...defaultProps}
        className={props.className}
        numberOfMonths={props.numberOfMonths}
        selectedDays={props.value || undefined}
        initialMonth={props.value || new Date()}
        disabledDays={props.disabledDays}
        firstDayOfWeek={1}
        onDayClick={props.onChange}
    />
}

export default DatePicker

interface RangePickerProps {
    className?: string,
    value?: DateTuple,
    numberOfMonths?: number,
    onChange?: (value: DateTuple) => void,
}

export const RangePicker: FunctionComponent<RangePickerProps> = props => {
    const [hovered, setHovered] = useState<Date>()
    const [from, to] = props.value || [undefined, undefined]

    const min = minDate(from, hovered)
    const max = maxDate(to || from, hovered)

    return <DayPicker
        {...defaultProps}
        className={clsx(props.className, styles.rangePicker)}
        numberOfMonths={props.numberOfMonths}
        modifiers={{
            [styles.start]: day => !!(from && (to? isSameDay(from, day): min && isSameDay(min, day))),
            [styles.end]: day => to? isSameDay(to, day): !!from && !!max && isSameDay(max, day),
            [styles.between]: day => (from && to)?
                day > from && day < to:
                !!min && !!max && day > min && day < max,
        }}
        disabledDays={[
            {
                after: new Date(),
            },
        ]}
        month={from ? new Date(from) : new Date()}
        onDayMouseEnter={(day) => setHovered(day)}
        onDayMouseLeave={() => setHovered(undefined)}
        onDayClick={(day) => {
            if (props.onChange) {
                if (!from) {
                    props.onChange([day, to])
                } else {
                    if(!to) {
                        if (day < from) {
                            props.onChange([day, from])
                        } else {
                            props.onChange([from, day])
                        }
                    } else {
                        props.onChange([day, undefined])
                    }
                }
            }
        }}
    />
}